import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  User,
  Mail,
  CheckCircle,
  XCircle,
} from "lucide-react";

import {
  GetUsers,
  ActivateUser,
  DeactivateUser,
} from "../Services/UserManagementService.js";

import NotificationAlert from "../../../../Alerts/NotificationAlert.jsx";

const ManageUsers = () => {
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [alert, setAlert] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // =========================
  // FETCH USERS
  // =========================
  const fetchUsers = async () => {
    try {
      setLoading(true);

      const res = await GetUsers();

      setUsers(res?.data?.users || []);
    } catch (error) {
      setAlert({
        type: "error",
        message:
          error?.response?.data?.message ||
          "Failed to fetch users. Please try again later.",
      });
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // =========================
  // ACTIVATE / DEACTIVATE
  // =========================
  const handleToggleStatus = async (user) => {
    try {
      setActionLoading(user.userUID);

      let res;

      if (user.isActive) {
        res = await DeactivateUser(user.userUID);
      } else {
        res = await ActivateUser(user.userUID);
      }

      if (res?.data?.status === "success") {
        setUsers((prev) =>
          prev.map((u) =>
            u.userUID === user.userUID
              ? {
                  ...u,
                  isActive: !u.isActive,
                }
              : u
          )
        );
      } else {
        setAlert({
          type: "error",
          message:
            res?.data?.message || "Failed to update user status.",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message:
          error?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // =========================
  // FILTER USERS
  // =========================
  const filteredUsers = useMemo(() => {
    return users.filter((item) => {
      const query = search.toLowerCase().trim();

      const matchesSearch =
        !query ||
        item.name?.toLowerCase().includes(query) ||
        item.email?.toLowerCase().includes(query) ||
        item.userUID?.toLowerCase().includes(query);

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "Active" && item.isActive) ||
        (statusFilter === "Inactive" && !item.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [users, search, statusFilter]);

  // =========================
  // PAGINATION
  // =========================
  const totalUsers = filteredUsers.length;

  const totalPages = Math.ceil(totalUsers / pageSize);

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;

    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page, pageSize]);

  // =========================
  // METRICS
  // =========================
  const activeUsers = users.filter((x) => x.isActive).length;

  const inactiveUsers = users.filter((x) => !x.isActive).length;

  // =========================
  // RESET PAGE WHEN FILTER
  // =========================
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }

    if (totalPages === 0 && page !== 1) {
      setPage(1);
    }
  }, [totalPages, page]);

  return (
    <>
      <div className="p-2 space-y-3">
        {/* ================= METRICS ================= */}
        <div className="grid grid-cols-3 gap-3">
          <MetricCard
            title="Total Users"
            value={users.length}
            icon={<User size={18} />}
          />

          <MetricCard
            title="Active"
            value={activeUsers}
            icon={<CheckCircle size={18} />}
            color="emerald"
          />

          <MetricCard
            title="Inactive"
            value={inactiveUsers}
            icon={<XCircle size={18} />}
            color="red"
          />
        </div>

        {/* ================= FILTER ================= */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm px-4 py-3">
          <div className="flex items-center gap-3">
            {/* SEARCH */}
            <div className="relative w-[320px]">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder="Search users..."
                className="w-full pl-10 pr-3 h-10 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>

            {/* STATUS FILTER */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
              className="h-10 px-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-700 min-w-[130px]"
            >
              <option value="">All Users</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>

            {/* ROWS */}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-500 whitespace-nowrap">
                Rows
              </span>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(Number(e.target.value));
                }}
                className="h-10 px-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-700 min-w-[80px]"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>

        {/* ================= TABLE ================= */}
        <div className="overflow-x-auto overflow-visible bg-white rounded-lg shadow-sm border border-gray-200">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
              <tr>
                <th className="p-3">User</th>
                <th className="p-3">Email</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>

            <tbody>
              {loading && initialLoading ? (
                <tr>
                  <td colSpan="3" className="p-6 text-center text-gray-500">
                    Loading users...
                  </td>
                </tr>
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan="3" className="p-6 text-center text-gray-500">
                    <User size={28} className="mx-auto text-gray-400 mb-3" />
                    No users found.
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((item) => (
                  <tr
                    key={item.userUID}
                    className="border-b hover:bg-gray-50 transition-colors"
                  >
                    {/* USER */}
                    <td className="p-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-xl bg-blue-50 flex items-center justify-center text-blue-700 font-semibold shrink-0">
                          {item.name?.charAt(0)?.toUpperCase() || "U"}
                        </div>

                        <div className="overflow-hidden">
                          <p className="font-medium text-gray-800 truncate">
                            {item.name || "-"}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            {item.userUID || "-"}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* EMAIL */}
                    <td className="p-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail size={13} className="text-gray-400 shrink-0" />
                        <span className="truncate">{item.email || "-"}</span>
                      </div>
                    </td>

                    {/* STATUS */}
                    <td className="p-3">
                      <button
                        onClick={() => handleToggleStatus(item)}
                        disabled={actionLoading === item.userUID}
                        aria-pressed={item.isActive}
                        title={item.isActive ? "Deactivate User" : "Activate User"}
                        className={`
                          relative w-[88px] h-7 rounded-full border-none overflow-hidden
                          transition-colors duration-300
                          ${
                            item.isActive
                              ? "bg-emerald-500"
                              : "bg-gray-300 dark:bg-gray-600"
                          }
                          ${
                            actionLoading === item.userUID
                              ? "opacity-55 cursor-not-allowed"
                              : ""
                          }
                        `}
                      >
                        {/* ACTIVE */}
                        <span
                          className={`
                            absolute inset-0 flex items-center justify-start pl-[11px]
                            text-[10.5px] font-bold tracking-wider text-white pointer-events-none
                            transition-all duration-200
                            ${
                              item.isActive
                                ? "opacity-100 translate-x-0"
                                : "opacity-0 -translate-x-1.5"
                            }
                          `}
                        >
                          ACTIVE
                        </span>

                        {/* INACTIVE */}
                        <span
                          className={`
                            absolute inset-0 flex items-center justify-end pr-[9px]
                            text-[10.5px] font-bold tracking-wider text-gray-700 dark:text-gray-100 pointer-events-none
                            transition-all duration-200
                            ${
                              item.isActive
                                ? "opacity-0 translate-x-1.5"
                                : "opacity-100 translate-x-0"
                            }
                          `}
                        >
                          INACTIVE
                        </span>

                        {/* CIRCLE */}
                        <div
                          className={`
                            absolute top-[3px] w-[22px] h-[22px] rounded-full bg-white
                            shadow-md pointer-events-none
                            transition-transform duration-300
                            ease-[cubic-bezier(0.34,1.45,0.64,1)]
                            ${
                              item.isActive
                                ? "translate-x-[63px]"
                                : "translate-x-[3px]"
                            }
                          `}
                        />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* ================= PAGINATION ================= */}
          {!initialLoading && totalPages >= 1 && (
            <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-white">
              <div className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
                {/* PREVIOUS */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((prev) => prev - 1)}
                  className={`
                    h-8 px-3 rounded-lg text-sm font-medium transition-all
                    ${
                      page === 1
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:bg-gray-100"
                    }
                  `}
                >
                  Prev
                </button>

                {/* PAGE NUMBERS */}
                {(() => {
                  const pages = [];

                  for (let i = 1; i <= totalPages; i++) {
                    if (
                      i === 1 ||
                      i === totalPages ||
                      (i >= page - 1 && i <= page + 1)
                    ) {
                      pages.push(i);
                    }
                  }

                  return pages.map((pageNumber, index) => {
                    const prevPage = pages[index - 1];

                    return (
                      <React.Fragment key={pageNumber}>
                        {prevPage && pageNumber - prevPage > 1 && (
                          <span className="px-1 text-sm text-gray-400">
                            ...
                          </span>
                        )}

                        <button
                          onClick={() => setPage(pageNumber)}
                          className={`
                            h-8 min-w-[34px] px-2 rounded-lg text-sm font-semibold transition-all
                            ${
                              page === pageNumber
                                ? "bg-primary text-white shadow-sm"
                                : "text-gray-600 hover:bg-gray-100"
                            }
                          `}
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  });
                })()}

                {/* NEXT */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((prev) => prev + 1)}
                  className={`
                    h-8 px-3 rounded-lg text-sm font-medium transition-all
                    ${
                      page === totalPages
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:bg-gray-100"
                    }
                  `}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ================= ALERT ================= */}
      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

// ============================================================
// METRIC CARD
// ============================================================

const MetricCard = ({ title, value, icon, color = "blue" }) => {
  const colorMap = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    red: "bg-red-50 text-red-600",
  };

  return (
    <div className="bg-white border border-gray-200 rounded-3xl shadow-sm p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-3xl font-semibold text-gray-900 mt-2">
            {value}
          </h3>
        </div>

        <div
          className={`h-12 w-12 rounded-3xl flex items-center justify-center ${
            colorMap[color] || colorMap.blue
          }`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
};

export default ManageUsers;